import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useCurrentOrgId, useCurrentUser } from '../auth';
import { toast } from "@/hooks/use-toast";
import { notificationsCountKey, notificationsListPrefix, type Notification } from "./useNotifications";
import { useNotificationPreferences, type QuietHours } from "./useNotificationPreferences";

const TOAST_WINDOW_MS = 5000;
const INVALIDATE_COALESCE_MS = 1000;

/** Compara o horário atual (na tz do usuário) com a janela de silêncio, com wrap de meia-noite. */
function isInQuietHours(quiet?: QuietHours | null): boolean {
  if (!quiet?.enabled || !quiet.start || !quiet.end) return false;
  try {
    const now = new Intl.DateTimeFormat("en-GB", {
      timeZone: quiet.tz || "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date());
    if (quiet.start <= quiet.end) return now >= quiet.start && now < quiet.end;
    return now >= quiet.start || now < quiet.end;
  } catch {
    return false;
  }
}

export function useRealtimeNotifications() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { user } = useCurrentUser();
  const userId = user?.user_id;
  const { preferences, isChannelEnabled } = useNotificationPreferences();

  // Refs para o handler do canal ler preferências atuais sem recriar a
  // subscription a cada mudança (o effect não depende de preferences).
  const prefsRef = useRef({ isChannelEnabled, quiet: preferences?.quiet_hours ?? null });
  prefsRef.current = { isChannelEnabled, quiet: preferences?.quiet_hours ?? null };

  // Throttle de toasts: 1 toast abre uma janela de 5s; o que chegar dentro dela
  // acumula e vira um único "+N novas notificações" no fim. Rajadas de realtime
  // (rollup, backfill, redistribuição) deixam de virar pilha de toasts.
  const burstRef = useRef<{ pending: number; timer: ReturnType<typeof setTimeout> | null }>({
    pending: 0,
    timer: null,
  });

  useEffect(() => {
    if (!orgId || !userId) return;

    // Coalesce rajadas: cada evento realtime vira no máximo 1 refetch (lista +
    // contagem) por janela. Sem isso, um DELETE/UPDATE em massa dispara 2
    // queries POR LINHA em cada navegador aberto — em 24/09 um "limpar lidas"
    // gerou ~50 mil req em 5 min e esgotou o pool do PostgREST (CRM fora).
    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
    const invalidate = () => {
      if (invalidateTimer) return;
      invalidateTimer = setTimeout(() => {
        invalidateTimer = null;
        queryClient.invalidateQueries({ queryKey: notificationsListPrefix(orgId, userId) });
        queryClient.invalidateQueries({ queryKey: notificationsCountKey(orgId, userId) });
      }, INVALIDATE_COALESCE_MS);
    };

    // DELETE chega para TODOS os assinantes da tabela (não aceita filter e o
    // payload só traz a PK). Só vale rebuscar se a linha apagada está no cache
    // deste usuário — senão é notificação de outra pessoa (ou já removida pelo
    // optimistic update de quem apagou) e não há nada a atualizar aqui.
    const isCached = (id: string) =>
      queryClient
        .getQueriesData<InfiniteData<Notification[]>>({ queryKey: notificationsListPrefix(orgId, userId) })
        .some(([, data]) => data?.pages?.some((page) => page.some((n) => n.id === id)));

    const flushBurst = () => {
      const burst = burstRef.current;
      burst.timer = null;
      if (burst.pending === 0) return;
      const count = burst.pending;
      burst.pending = 0;
      toast({
        title: `+${count} novas notificações`,
        description: "Abra o sino para ver.",
        duration: 5000,
      });
      burst.timer = setTimeout(flushBurst, TOAST_WINDOW_MS);
    };

    const showToast = (notification: Notification) => {
      const { isChannelEnabled: channelEnabled, quiet } = prefsRef.current;
      const category = notification.category ?? "system";
      // O servidor só aplica preferências/quiet hours aos canais externos;
      // o toast in-app é responsabilidade do cliente.
      if (!channelEnabled(category, "in_app")) return;
      if (isInQuietHours(quiet)) return;

      const urgent = notification.priority === "urgent" || notification.priority === "high";
      const burst = burstRef.current;

      if (urgent) {
        // Prioridade alta fura o throttle e não consome a janela.
        toast({ title: notification.title, description: notification.message, duration: 5000 });
        return;
      }

      if (burst.timer) {
        burst.pending += 1;
        return;
      }
      toast({ title: notification.title, description: notification.message, duration: 5000 });
      burst.timer = setTimeout(flushBurst, TOAST_WINDOW_MS);
    };

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          invalidate();
          const notification = payload.new as Notification | undefined;
          if (notification) showToast(notification);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        invalidate
      )
      .on(
        "postgres_changes",
        {
          // Sem filter: o payload de DELETE só carrega a PK (sem REPLICA IDENTITY
          // FULL o filtro por user_id nunca casaria e o evento não chegaria).
          // Por isso o evento chega de TODOS os usuários — filtramos pelo cache.
          event: "DELETE",
          schema: "public",
          table: "notifications",
        },
        (payload) => {
          const id = (payload.old as { id?: string } | undefined)?.id;
          if (id && isCached(id)) invalidate();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (invalidateTimer) clearTimeout(invalidateTimer);
      const burst = burstRef.current;
      if (burst.timer) clearTimeout(burst.timer);
      burst.timer = null;
      burst.pending = 0;
    };
  }, [orgId, userId, queryClient]);
}
